import { useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaCheckCircle } from 'react-icons/fa'

import { useQuery } from '@tanstack/react-query'

import api from 'services/api'

import { IUser, useUser } from 'contexts/user.context'

import ProfileItineraryCard from 'components/profileItineraryCard'
import ProfileTravelMap from 'components/profileTravelMap'
import RankingIcon from 'components/rankingIcon'
import TravelInterestIcon from 'components/travelInterestIcon'

import * as P from '../Profile/modernProfile'

const DEFAULT_TEXT =
  'Viajante RedSterna que busca viver e compartilhar experiências incríveis ao redor do mundo! Siga-me para acompanhar minha jornada.'

const normalizeCityName = (name: unknown) =>
  String(name ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

const countDestinations = (itineraries: any[] = []) => {
  const destinations = new Set<string>()

  itineraries.forEach((itinerary) => {
    ;(itinerary.cities ?? []).forEach((city: any) => {
      const cityName = normalizeCityName(city.name)

      if (cityName) {
        destinations.add(cityName)
        return
      }

      const latitude = Number(city.latitude)
      const longitude = Number(city.longitude)

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        destinations.add(`${latitude.toFixed(4)}|${longitude.toFixed(4)}`)
      }
    })
  })

  return destinations.size
}

const getSternaPreposition = (gender?: string) => {
  if (gender === 'F') return 'da'
  if (gender === 'M') return 'do'
  return 'de'
}

const User = () => {
  const history = useHistory()
  const { user: currentUser } = useUser()
  const { id } = useParams<{ id: string }>()
  const [fetch, setFetch] = useState(true)
  const [aboutExpanded, setAboutExpanded] = useState(false)

  const {
    data: userInfo,
    isLoading: isLoadingUserInfo,
    isError: isUserInfoError
  } = useQuery({
    queryKey: ['userData', id],
    queryFn: async () => {
      const { data }: { data: IUser } = await api.get(
        `/users/${id}/itineraries`
      )
      setFetch(true)
      return data
    },
    enabled: !!id && fetch,
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const { data: itineraries = [] } = useQuery({
    queryKey: ['publicUserItineraries', id],
    queryFn: async () => {
      const { data } = await api.get(`/itineraries/user/${id}`)
      return Array.isArray(data) ? data : []
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  })

  const { data: inspirationsCount = 0 } = useQuery({
    queryKey: ['publicInspirationsCount', id],
    queryFn: async () => {
      const { data } = await api.get(
        `/itineraries/user/${id}/inspirations-count`
      )
      return Number(data?.count ?? 0)
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  })

  const { data: following = [] } = useQuery({
    queryKey: ['userFollowing', id],
    queryFn: async () => {
      const { data } = await api.get(`/user-follower/following/${id}`)
      return data
    },
    enabled: !!id,
    staleTime: 15 * 60 * 1000
  })

  const { data: followers = [], refetch: refetchFollowers } = useQuery({
    queryKey: ['userFollowers', id],
    queryFn: async () => {
      const { data } = await api.get(`/user-follower/followers/${id}`)
      return data
    },
    enabled: !!id,
    staleTime: 15 * 60 * 1000
  })

  const isFollowing = Boolean(
    currentUser?.id &&
      followers.some((relation: any) => relation.followerId === currentUser.id)
  )

  const toggleFollow = useCallback(async (): Promise<void> => {
    if (!currentUser?.id) {
      toast.info('Entre na sua conta para seguir outros viajantes.')
      return
    }

    try {
      if (isFollowing) {
        await api.delete(`/user-follower/follow/${id}`)
        toast.success('Você deixou de seguir este viajante.')
      } else {
        await api.post('/user-follower/follow', { followId: id })
        toast.success('Seguindo com sucesso!')
      }

      await refetchFollowers()
    } catch (error) {
      toast.error('Não foi possível atualizar essa conexão. Tente novamente.')
    }
  }, [currentUser?.id, id, isFollowing, refetchFollowers])

  if (isLoadingUserInfo) return <h1>Carregando</h1>

  if (isUserInfoError || !userInfo) {
    return <h1>Este perfil não está disponível.</h1>
  }

  const destinationCount = countDestinations(itineraries)
  const isOwnProfile = currentUser?.id === userInfo.id
  const preposition = getSternaPreposition(userInfo.gender)
  const about = userInfo.about?.trim() || DEFAULT_TEXT

  return (
    <P.Page>
      <P.TopLabel>
        Sterna {preposition} {userInfo.firstname}
      </P.TopLabel>

      <P.Cover>
        <P.CoverImage
          src={userInfo.coverPicture || '/images/cover-empty.png'}
          alt="Foto de capa"
          onError={(event) => {
            const fallback = '/images/cover-empty.png'
            if (!event.currentTarget.src.endsWith(fallback)) {
              event.currentTarget.src = fallback
            }
          }}
        />
        <P.SearchButton onClick={() => history.push('/buscar-usuarios')}>
          Procurar usuário
          <img src="/images/user-search.svg" alt="Buscar usuários" />
        </P.SearchButton>
      </P.Cover>

      <P.IdentityRow>
        <P.Avatar
          src={userInfo.profilePicture || '/images/profile-empty.png'}
          alt={`Foto de ${userInfo.firstname}`}
        />
        <P.Identity>
          <P.NameLine>
            <P.Name>
              {isOwnProfile
                ? `Bem vindo, ${userInfo.firstname}!`
                : userInfo.firstname}
            </P.Name>
            {userInfo.verified && (
              <P.VerifiedBadge title="Perfil verificado pela RedSterna">
                <FaCheckCircle />
                Verificado
              </P.VerifiedBadge>
            )}
          </P.NameLine>
          <P.Subtitle>
            Explorando o mundo, um roteiro de cada vez. ✈️
          </P.Subtitle>
        </P.Identity>

        {!isOwnProfile && (
          <P.SocialActions>
            <P.FollowButton onClick={toggleFollow}>
              {isFollowing ? 'Seguindo' : 'Seguir'}
            </P.FollowButton>
          </P.SocialActions>
        )}

        <P.Stats>
          <P.Stat>
            <strong>{followers.length}</strong>
            <span>Seguidores</span>
          </P.Stat>
          <P.Stat>
            <strong>{following.length}</strong>
            <span>Seguindo</span>
          </P.Stat>
          <P.Stat>
            <strong>{itineraries.length}</strong>
            <span>Roteiros</span>
          </P.Stat>
          <P.Stat>
            <strong>{destinationCount}</strong>
            <span>Destinos</span>
          </P.Stat>
          <P.InspirationStat>
            <strong>{inspirationsCount}</strong>
            <span>Inspirações</span>
          </P.InspirationStat>
        </P.Stats>
      </P.IdentityRow>

      <P.InfoRibbon>
        <P.InfoBlock>
          <P.InfoContent>
            <P.InfoTitle>Sobre mim</P.InfoTitle>
            <P.AboutText
              type="button"
              $expanded={aboutExpanded}
              aria-expanded={aboutExpanded}
              title={
                aboutExpanded ? 'Clique para recolher' : 'Clique para ler tudo'
              }
              onClick={() => setAboutExpanded((expanded) => !expanded)}
            >
              {about}
            </P.AboutText>
          </P.InfoContent>
        </P.InfoBlock>

        <P.RankBlock>
          <P.InfoTitle>Perfil</P.InfoTitle>
          <RankingIcon itinerariesLength={itineraries.length} />
        </P.RankBlock>

        <P.InfoBlock>
          <P.InfoContent>
            <P.InfoTitle>Interesses</P.InfoTitle>
            <P.Interests>
              {userInfo.interests?.map((interest: any) => {
                const value = interest.name
                const label = interest.description || value

                return (
                  <P.Interest
                    key={interest.id ?? value}
                    title={label}
                    aria-label={label}
                  >
                    <TravelInterestIcon value={value} title={label} />
                  </P.Interest>
                )
              })}
            </P.Interests>
          </P.InfoContent>
        </P.InfoBlock>
      </P.InfoRibbon>

      <ProfileTravelMap
        itineraries={itineraries}
        ownerName={userInfo.firstname}
      />

      {itineraries.length > 0 && (
        <P.Section>
          <P.SectionHeader>
            <h2>Roteiros publicados ({itineraries.length})</h2>
          </P.SectionHeader>
          <P.CardsGrid>
            {itineraries.map((itinerary: any) => (
              <ProfileItineraryCard key={itinerary.id} itinerary={itinerary} />
            ))}
          </P.CardsGrid>
        </P.Section>
      )}
    </P.Page>
  )
}

export default User
